import React, { useState, useEffect } from 'react';
import { Upload, Check, AlertCircle, Clock, RefreshCw, Trash2, Wifi, WifiOff, Loader2 } from 'lucide-react';
import { uploadQueueService } from '../../services/upload-queue.service';
import { uploadQueueDB, QueuedUpload, QueuedFile } from '../../db/upload-queue.db';
import { isNativeApp } from '../../utils/platform.utils';

interface UploadItem extends QueuedUpload {
  files?: QueuedFile[];
}

interface PendingNativeUpload {
  key: string;
  type: string;
  blob_name: string;
  job_id?: number;
  capture_time: string;
  retryCount?: number;
  status: 'finalizing' | 'queued';  // finalizing = uploaded to blob, queued = waiting for network
  filename?: string;
}

const translations = {
  en: {
    title: 'Upload Status',
    online: 'Online',
    offline: 'Offline',
    pending: 'Pending',
    uploading: 'Uploading',
    failed: 'Failed',
    completed: 'Completed',
    noUploads: 'No pending uploads',
    allSynced: 'All uploads synced!',
    retry: 'Retry',
    delete: 'Delete',
    retryAll: 'Retry All Failed',
    clearAll: 'Clear Failed',
    queueStats: 'Queue Statistics',
    uploadType: 'Type',
    createdAt: 'Created',
    files: 'Files',
    error: 'Error',
    platform: 'Platform',
    native: 'Native (Background)',
    pwa: 'PWA (Foreground)',
    processingQueue: 'Processing queue...',
    refresh: 'Refresh',
    truckTicket: 'Truck Ticket',
    dailyReport: 'Daily Report',
    asphaltReport: 'Asphalt Report',
    tmTicket: 'T&M Ticket',
    equipmentIssue: 'Equipment Issue',
    photo: 'Photo',
    equipmentRepair: 'Equipment Repair',
    nativeUploads: 'Background Uploads (Native)',
    captureTime: 'Captured',
    submittedAt: 'Submitted',
    waitingFinalize: 'Finalizing...',
    waitingNetwork: 'Waiting for network...',
    blobName: 'File',
  },
  es: {
    title: 'Estado de Carga',
    online: 'En línea',
    offline: 'Sin conexión',
    pending: 'Pendiente',
    uploading: 'Cargando',
    failed: 'Fallido',
    completed: 'Completado',
    noUploads: 'No hay cargas pendientes',
    allSynced: '¡Todas las cargas sincronizadas!',
    retry: 'Reintentar',
    delete: 'Eliminar',
    retryAll: 'Reintentar Todos',
    clearAll: 'Limpiar Fallidos',
    queueStats: 'Estadísticas de Cola',
    uploadType: 'Tipo',
    createdAt: 'Creado',
    files: 'Archivos',
    error: 'Error',
    platform: 'Plataforma',
    native: 'Nativo (Fondo)',
    pwa: 'PWA (Primer Plano)',
    processingQueue: 'Procesando cola...',
    refresh: 'Actualizar',
    truckTicket: 'Ticket de Camión',
    dailyReport: 'Reporte Diario',
    asphaltReport: 'Reporte de Asfalto',
    tmTicket: 'Ticket T&M',
    equipmentIssue: 'Problema de Equipo',
    photo: 'Foto',
    equipmentRepair: 'Reparación de Equipo',
    nativeUploads: 'Cargas en Segundo Plano (Nativo)',
    captureTime: 'Capturado',
    submittedAt: 'Enviado',
    waitingFinalize: 'Finalizando...',
    waitingNetwork: 'Esperando red...',
    blobName: 'Archivo',
  }
};

export const UploadStatus: React.FC = () => {
  const [uploads, setUploads] = useState<UploadItem[]>([]);
  const [nativeUploads, setNativeUploads] = useState<PendingNativeUpload[]>([]);
  const [stats, setStats] = useState({ pending: 0, uploading: 0, failed: 0, completed: 0, total: 0 });
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [language, setLanguage] = useState<'en' | 'es'>(() => {
    return (localStorage.getItem('language') as 'en' | 'es') || 'en';
  });

  const t = translations[language];

  // Load pending native background uploads from localStorage
  const loadNativeUploads = () => {
    const pending: PendingNativeUpload[] = [];

    // Check for uploads waiting to be finalized (blob uploaded, needs DB record)
    const finalizeKeys = Object.keys(localStorage).filter(k => k.startsWith('pending_finalize_'));
    for (const key of finalizeKeys) {
      try {
        const data = JSON.parse(localStorage.getItem(key) || '{}');
        if (data.blob_name) {
          pending.push({
            key,
            type: data.type || 'unknown',
            blob_name: data.blob_name,
            job_id: data.job_id,
            capture_time: data.capture_time || new Date().toISOString(),
            retryCount: data.retryCount || 0,
            status: 'finalizing',
          });
        }
      } catch (e) {
        console.error('Failed to parse pending finalize:', key);
      }
    }

    // Check for uploads queued due to network errors (waiting to upload to blob)
    const queuedKeys = Object.keys(localStorage).filter(k => k.startsWith('pending_native_upload_'));
    for (const key of queuedKeys) {
      try {
        const data = JSON.parse(localStorage.getItem(key) || '{}');
        if (data.filename || data.filePath) {
          pending.push({
            key,
            type: data.options?.type || 'unknown',
            blob_name: data.filename || 'Queued upload',
            job_id: data.options?.jobId,
            capture_time: data.createdAt || new Date().toISOString(),
            retryCount: data.retryCount || 0,
            status: 'queued',
            filename: data.filename,
          });
        }
      } catch (e) {
        console.error('Failed to parse queued upload:', key);
      }
    }

    setNativeUploads(pending);
  };

  const loadUploads = async () => {
    try {
      // Get all uploads
      const allUploads = await uploadQueueDB.uploads.toArray();

      // Get files for each upload
      const uploadsWithFiles: UploadItem[] = await Promise.all(
        allUploads.map(async (upload) => {
          const files = upload.id
            ? await uploadQueueDB.files.where('uploadId').equals(upload.id).toArray()
            : [];
          return { ...upload, files };
        })
      );

      // Sort by status (uploading first, then pending, then failed) and then by date
      uploadsWithFiles.sort((a, b) => {
        const statusOrder = { uploading: 0, pending: 1, failed: 2, completed: 3 };
        const statusDiff = statusOrder[a.status] - statusOrder[b.status];
        if (statusDiff !== 0) return statusDiff;
        return (b.createdAt?.getTime() || 0) - (a.createdAt?.getTime() || 0);
      });

      setUploads(uploadsWithFiles);

      // Get stats
      const queueStats = await uploadQueueService.getQueueStats();
      setStats(queueStats);
    } catch (error) {
      console.error('Failed to load uploads:', error);
    }
  };

  useEffect(() => {
    loadUploads();
    loadNativeUploads();

    // Refresh every 5 seconds
    const interval = setInterval(() => {
      loadUploads();
      loadNativeUploads();
    }, 5000);

    // Listen for online/offline
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    // Listen for language changes
    const handleLanguageChange = (e: CustomEvent) => {
      setLanguage(e.detail as 'en' | 'es');
    };
    window.addEventListener('languageChange', handleLanguageChange as EventListener);

    return () => {
      clearInterval(interval);
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
      window.removeEventListener('languageChange', handleLanguageChange as EventListener);
    };
  }, []);

  const handleRefresh = async () => {
    setIsRefreshing(true);
    await uploadQueueService.processQueue();
    await loadUploads();
    loadNativeUploads();
    setIsRefreshing(false);
  };

  const handleRetry = async (uploadId: number) => {
    await uploadQueueService.retryUpload(uploadId);
    await loadUploads();
  };

  const handleDelete = async (uploadId: number) => {
    await uploadQueueService.deleteUpload(uploadId);
    await loadUploads();
  };

  const handleRetryAllFailed = async () => {
    const failedUploads = uploads.filter(u => u.status === 'failed');
    for (const upload of failedUploads) {
      if (upload.id) {
        await uploadQueueService.retryUpload(upload.id);
      }
    }
    await loadUploads();
  };

  const handleClearAllFailed = async () => {
    const failedUploads = uploads.filter(u => u.status === 'failed');
    for (const upload of failedUploads) {
      if (upload.id) {
        await uploadQueueService.deleteUpload(upload.id);
      }
    }
    await loadUploads();
  };

  const getTypeLabel = (type: string): string => {
    const typeMap: Record<string, keyof typeof t> = {
      truck_ticket: 'truckTicket',
      daily_report: 'dailyReport',
      asphalt_report: 'asphaltReport',
      tm_ticket: 'tmTicket',
      equipment_issue: 'equipmentIssue',
      photo: 'photo',
      equipment_repair: 'equipmentRepair',
      equipment_repair_update: 'equipmentRepair',
    };
    return t[typeMap[type] || 'photo'] || type;
  };

  const getStatusColor = (status: string): string => {
    switch (status) {
      case 'pending': return '#f59e0b';
      case 'uploading': return '#3b82f6';
      case 'failed': return '#ef4444';
      case 'completed': return '#10b981';
      default: return '#6b7280';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'pending': return <Clock size={18} />;
      case 'uploading': return <Loader2 size={18} className="spin" />;
      case 'failed': return <AlertCircle size={18} />;
      case 'completed': return <Check size={18} />;
      default: return <Upload size={18} />;
    }
  };

  const formatDate = (date: Date | undefined): string => {
    if (!date) return 'Unknown';
    return new Intl.DateTimeFormat(language === 'es' ? 'es-ES' : 'en-US', {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    }).format(date);
  };

  const formatDateTime = (dateString: string | undefined): string => {
    if (!dateString) return 'Unknown';
    try {
      const date = new Date(dateString);
      return new Intl.DateTimeFormat(language === 'es' ? 'es-ES' : 'en-US', {
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        second: '2-digit',
      }).format(date);
    } catch {
      return dateString;
    }
  };

  return (
    <div style={styles.container}>
      {/* Header */}
      <div style={styles.header}>
        <div style={styles.headerTop}>
          <div style={styles.titleRow}>
            <Upload size={28} color="#dc2626" />
            <h1 style={styles.title}>{t.title}</h1>
          </div>
          <button onClick={handleRefresh} style={styles.refreshButton} disabled={isRefreshing}>
            <RefreshCw size={20} className={isRefreshing ? 'spin' : ''} />
          </button>
        </div>

        {/* Online Status */}
        <div style={styles.statusRow}>
          {isOnline ? (
            <div style={styles.onlineStatus}>
              <Wifi size={16} color="#10b981" />
              <span style={{ color: '#10b981' }}>{t.online}</span>
            </div>
          ) : (
            <div style={styles.offlineStatus}>
              <WifiOff size={16} color="#ef4444" />
              <span style={{ color: '#ef4444' }}>{t.offline}</span>
            </div>
          )}
          <div style={styles.platformBadge}>
            {isNativeApp() ? t.native : t.pwa}
          </div>
        </div>
      </div>

      {/* Stats Cards - include native uploads in counts */}
      <div style={styles.statsGrid}>
        <div style={{ ...styles.statCard, borderLeft: '4px solid #f59e0b' }}>
          <div style={styles.statNumber}>
            {stats.pending + nativeUploads.filter(u => u.status === 'queued').length}
          </div>
          <div style={styles.statLabel}>{t.pending}</div>
        </div>
        <div style={{ ...styles.statCard, borderLeft: '4px solid #3b82f6' }}>
          <div style={styles.statNumber}>
            {stats.uploading + nativeUploads.filter(u => u.status === 'finalizing').length}
          </div>
          <div style={styles.statLabel}>{t.uploading}</div>
        </div>
        <div style={{ ...styles.statCard, borderLeft: '4px solid #ef4444' }}>
          <div style={styles.statNumber}>{stats.failed}</div>
          <div style={styles.statLabel}>{t.failed}</div>
        </div>
      </div>

      {/* Action Buttons */}
      {stats.failed > 0 && (
        <div style={styles.actionButtons}>
          <button onClick={handleRetryAllFailed} style={styles.retryAllButton}>
            <RefreshCw size={16} />
            {t.retryAll}
          </button>
          <button onClick={handleClearAllFailed} style={styles.clearAllButton}>
            <Trash2 size={16} />
            {t.clearAll}
          </button>
        </div>
      )}

      {/* Native Background Uploads (iOS/Android) */}
      {isNativeApp() && nativeUploads.length > 0 && (
        <div style={styles.nativeSection}>
          <h3 style={styles.sectionTitle}>{t.nativeUploads} ({nativeUploads.length})</h3>
          {nativeUploads.map((upload) => (
            <div key={upload.key} style={{
              ...styles.nativeCard,
              borderLeft: `4px solid ${upload.status === 'queued' ? '#f59e0b' : '#8b5cf6'}`
            }}>
              <div style={styles.nativeHeader}>
                <div style={{
                  ...styles.statusBadge,
                  backgroundColor: upload.status === 'queued' ? '#f59e0b' : '#8b5cf6'
                }}>
                  {upload.status === 'queued' ? (
                    <WifiOff size={16} />
                  ) : (
                    <Loader2 size={16} className="spin" />
                  )}
                  <span>{upload.status === 'queued' ? t.waitingNetwork : t.waitingFinalize}</span>
                </div>
              </div>
              <div style={styles.nativeBody}>
                <div><strong>{t.uploadType}:</strong> {getTypeLabel(upload.type)}</div>
                <div><strong>{t.captureTime}:</strong> {formatDateTime(upload.capture_time)}</div>
                <div style={styles.blobName}><strong>{t.blobName}:</strong> {upload.filename || upload.blob_name}</div>
                {upload.retryCount && upload.retryCount > 0 && (
                  <div style={styles.retryCount}>Retry #{upload.retryCount}</div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Upload List */}
      <div style={styles.uploadList}>
        {uploads.length === 0 && nativeUploads.length === 0 ? (
          <div style={styles.emptyState}>
            <Check size={48} color="#10b981" />
            <p style={styles.emptyText}>{t.allSynced}</p>
          </div>
        ) : uploads.length === 0 ? null : (
          uploads.map((upload) => (
            <div key={upload.id} style={styles.uploadCard}>
              <div style={styles.uploadHeader}>
                <div style={{ ...styles.statusBadge, backgroundColor: getStatusColor(upload.status) }}>
                  {getStatusIcon(upload.status)}
                  <span>{t[upload.status as keyof typeof t] || upload.status}</span>
                </div>
                <span style={styles.uploadDate}>{formatDate(upload.createdAt)}</span>
              </div>

              <div style={styles.uploadBody}>
                <div style={styles.uploadType}>
                  <strong>{t.uploadType}:</strong> {getTypeLabel(upload.type)}
                </div>
                <div style={styles.timestampRow}>
                  <strong>{t.submittedAt}:</strong> {upload.createdAt ? new Intl.DateTimeFormat(language === 'es' ? 'es-ES' : 'en-US', {
                    month: 'short',
                    day: 'numeric',
                    hour: 'numeric',
                    minute: '2-digit',
                    second: '2-digit',
                  }).format(upload.createdAt) : 'Unknown'}
                </div>
                {upload.files && upload.files.length > 0 && (
                  <div style={styles.uploadFiles}>
                    <strong>{t.files}:</strong> {upload.files.length} ({upload.files.map(f => f.filename).join(', ')})
                  </div>
                )}
                {upload.error && (
                  <div style={styles.uploadError}>
                    <AlertCircle size={14} />
                    <span>{upload.error}</span>
                  </div>
                )}
                {upload.retryCount > 0 && (
                  <div style={styles.retryCount}>
                    Retry #{upload.retryCount}
                  </div>
                )}
              </div>

              {(upload.status === 'failed' || upload.status === 'pending') && (
                <div style={styles.uploadActions}>
                  <button onClick={() => upload.id && handleRetry(upload.id)} style={styles.retryButton}>
                    <RefreshCw size={14} />
                    {t.retry}
                  </button>
                  <button onClick={() => upload.id && handleDelete(upload.id)} style={styles.deleteButton}>
                    <Trash2 size={14} />
                    {t.delete}
                  </button>
                </div>
              )}
            </div>
          ))
        )}
      </div>

      <style>{`
        .spin {
          animation: spin 1s linear infinite;
        }
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

const styles: { [key: string]: React.CSSProperties } = {
  container: {
    minHeight: '100vh',
    backgroundColor: '#f9fafb',
    paddingBottom: '100px',
  },
  header: {
    backgroundColor: 'white',
    padding: '20px',
    borderBottom: '2px solid #e5e7eb',
  },
  headerTop: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '12px',
  },
  titleRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
  },
  title: {
    fontSize: '24px',
    fontWeight: '700',
    margin: 0,
    color: '#111827',
  },
  refreshButton: {
    padding: '8px',
    backgroundColor: '#f3f4f6',
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  statusRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
  },
  onlineStatus: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    fontSize: '14px',
    fontWeight: '500',
  },
  offlineStatus: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    fontSize: '14px',
    fontWeight: '500',
  },
  platformBadge: {
    fontSize: '12px',
    padding: '4px 8px',
    backgroundColor: '#e5e7eb',
    borderRadius: '4px',
    color: '#6b7280',
  },
  statsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, 1fr)',
    gap: '12px',
    padding: '16px',
  },
  statCard: {
    backgroundColor: 'white',
    padding: '16px',
    borderRadius: '12px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
  },
  statNumber: {
    fontSize: '28px',
    fontWeight: '700',
    color: '#111827',
  },
  statLabel: {
    fontSize: '14px',
    color: '#6b7280',
    marginTop: '4px',
  },
  actionButtons: {
    display: 'flex',
    gap: '12px',
    padding: '0 16px 16px',
  },
  retryAllButton: {
    flex: 1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
    padding: '12px',
    backgroundColor: '#3b82f6',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    fontSize: '14px',
    fontWeight: '600',
    cursor: 'pointer',
  },
  clearAllButton: {
    flex: 1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
    padding: '12px',
    backgroundColor: '#ef4444',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    fontSize: '14px',
    fontWeight: '600',
    cursor: 'pointer',
  },
  uploadList: {
    padding: '0 16px',
  },
  emptyState: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '48px 24px',
    backgroundColor: 'white',
    borderRadius: '12px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
  },
  emptyText: {
    marginTop: '16px',
    fontSize: '18px',
    color: '#10b981',
    fontWeight: '600',
  },
  uploadCard: {
    backgroundColor: 'white',
    borderRadius: '12px',
    padding: '16px',
    marginBottom: '12px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
  },
  uploadHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '12px',
  },
  statusBadge: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    padding: '4px 10px',
    borderRadius: '20px',
    color: 'white',
    fontSize: '13px',
    fontWeight: '600',
  },
  uploadDate: {
    fontSize: '13px',
    color: '#6b7280',
  },
  uploadBody: {
    fontSize: '14px',
    color: '#374151',
  },
  uploadType: {
    marginBottom: '6px',
  },
  uploadFiles: {
    marginBottom: '6px',
    fontSize: '13px',
    color: '#6b7280',
    wordBreak: 'break-all',
  },
  uploadError: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    color: '#ef4444',
    fontSize: '13px',
    marginTop: '8px',
  },
  retryCount: {
    fontSize: '12px',
    color: '#9ca3af',
    marginTop: '4px',
  },
  uploadActions: {
    display: 'flex',
    gap: '8px',
    marginTop: '12px',
    paddingTop: '12px',
    borderTop: '1px solid #e5e7eb',
  },
  retryButton: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    padding: '8px 12px',
    backgroundColor: '#3b82f6',
    color: 'white',
    border: 'none',
    borderRadius: '6px',
    fontSize: '13px',
    fontWeight: '500',
    cursor: 'pointer',
  },
  deleteButton: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    padding: '8px 12px',
    backgroundColor: '#ef4444',
    color: 'white',
    border: 'none',
    borderRadius: '6px',
    fontSize: '13px',
    fontWeight: '500',
    cursor: 'pointer',
  },
  nativeSection: {
    padding: '0 16px 16px',
  },
  sectionTitle: {
    fontSize: '16px',
    fontWeight: '600',
    color: '#6b7280',
    marginBottom: '12px',
  },
  nativeCard: {
    backgroundColor: 'white',
    borderRadius: '12px',
    padding: '16px',
    marginBottom: '12px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
    borderLeft: '4px solid #8b5cf6',
  },
  nativeHeader: {
    marginBottom: '12px',
  },
  nativeBody: {
    fontSize: '14px',
    color: '#374151',
    display: 'flex',
    flexDirection: 'column',
    gap: '6px',
  },
  blobName: {
    fontSize: '12px',
    color: '#9ca3af',
    wordBreak: 'break-all',
  },
  timestampRow: {
    fontSize: '13px',
    color: '#6b7280',
    marginBottom: '4px',
  },
};

export default UploadStatus;
